import { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  MapPin,
  Mic2,
  Calendar,
  Filter,
  X,
  Building,
  ShoppingCart,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VenueCard } from "@/components/venue-card";
import { LoadingSpinner } from "@/components/loading-spinner";
import {
  AdvancedFilterContent,
  type AdvancedFilters,
} from "@/components/advanced-filter";
import { Pagination } from "@/components/pagination";
import Navbar from "@/components/navbar";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { Link } from "wouter";
import {
  type VenueWithShows,
  type PaginatedResult,
  type ShowWithDetails,
} from "@shared/schema";
import { PlanStorage } from "@/lib/plan-storage";
import { usePlanSidebar } from "@/contexts/plan-sidebar-context";
import { BrowseLayout } from "@/components/layouts/browse-layout";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { applyAllFilters, trimEntityShows, type SearchFilters, type ShowFilterConfig } from "@shared/filter-engine";
import { calculateAudienceMatch } from "@/lib/audience-matching";

// Helper function to get default advanced filters
const getDefaultAdvancedFilters = (): AdvancedFilters => ({
  artistName: "",
  venueName: "",
  location: "",
  dateFrom: "",
  dateTo: "",
  genre: [],
  careerStage: [],
  setArtist: "",
  venueType: [],
  venueLocation: "",
  targetAudience: {
    fanGender: [],
    fanEthnicity: [],
    fanAge: [],
    fanIncome: [],
    fanAttributes: [],
  },
  minMatchPercentage: 0,
});

export default function BrowseVenues() {
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedShows, setSelectedShows] = useState<ShowWithDetails[]>([]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Single source of truth for all filters
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    const careerStageParam = params.get("careerStage");
    const venueTypeParam = params.get("venueType");
    return {
      ...getDefaultAdvancedFilters(),
      artistName: params.get("artistName") || "",
      venueName: params.get("venueName") || "",
      location: params.get("location") || "",
      genre: params.get("genre") ? [params.get("genre")!] : [],
      dateFrom: params.get("dateFrom") || "",
      dateTo: params.get("dateTo") || "",
      careerStage: careerStageParam ? careerStageParam.split(',') : [],
      venueType: venueTypeParam ? venueTypeParam.split(',') : [],
      setArtist: params.get("setArtist") || "",
    };
  });

  // Debounced filters derived from advancedFilters for API calls
  const [debouncedFilters, setDebouncedFilters] = useState(() => ({
    genre: advancedFilters.genre?.[0] || "",
    location: advancedFilters.location || "",
    dateFrom: advancedFilters.dateFrom || "",
    dateTo: advancedFilters.dateTo || "",
  }));

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [capacityFilter, setCapacityFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [itemsPerPage] = useState(20);
  const isInitialMount = useRef(true);

  // Delay turning off initial mount protection to allow all initialization to settle
  // (persona context, debounced filters, etc. all trigger after mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Load selected shows from localStorage on mount
  useEffect(() => {
    const storedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(storedShows);
  }, [user?.id]);

  // Listen for proposal storage changes from modals
  useEffect(() => {
    const handleStorageChange = () => {
      const storedShows = PlanStorage.getSelectedShows(user?.id);
      setSelectedShows(storedShows);
    };

    window.addEventListener("proposal-storage-changed", handleStorageChange);
    return () =>
      window.removeEventListener(
        "proposal-storage-changed",
        handleStorageChange,
      );
  }, [user?.id]);

  // Track browse venues page visit for achievements
  useEffect(() => {
    if (user) {
      const trackPageVisit = async () => {
      };
      trackPageVisit();
    }
  }, [user]);

  // Apply active persona filters
  useEffect(() => {
    if (activePersona) {
      const personaFilters: AdvancedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        },
        // NOTE: targetLocations are now converted to DMA IDs for hard filtering
        // They no longer contribute to location string or percentage match
      };
      setAdvancedFilters(personaFilters);
    } else {
      // Clear audience filters when persona is removed
      const clearedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: [],
          fanEthnicity: [],
          fanAge: [],
          fanIncome: [],
          fanAttributes: [],
        },
      };
      setAdvancedFilters(clearedFilters);
    }
  }, [activePersona]);

  // Build query parameters for server-side filtering
  // Uses debouncedFilters for text inputs (genre, location, dates) to prevent rapid API calls
  // NOTE: personaFilters are NOT sent to server — demographics are fetched separately
  // and persona filtering is done entirely client-side (consistent with browse-artists behavior)
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    // Basic filters - use debouncedFilters for text inputs to prevent rapid API calls
    if (debouncedFilters.genre) params.set('genre', debouncedFilters.genre);
    if (debouncedFilters.location) params.set('location', debouncedFilters.location);
    if (debouncedFilters.dateFrom) params.set('dateFrom', debouncedFilters.dateFrom);
    if (debouncedFilters.dateTo) params.set('dateTo', debouncedFilters.dateTo);
    if (advancedFilters.venueName) params.set('name', advancedFilters.venueName);
    if (advancedFilters.careerStage?.length) params.set('careerStage', advancedFilters.careerStage.join(','));
    if (advancedFilters.setArtist) params.set('setArtist', advancedFilters.setArtist);

    // DMA hard filter from persona
    if (activePersona?.targetDmaIds?.length) {
      params.set('targetDmaIds', JSON.stringify(activePersona.targetDmaIds));
    }

    return params.toString();
  }, [debouncedFilters, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.setArtist, activePersona]);

  const {
    data: allVenues,
    isLoading: isVenuesLoading,
    error,
  } = useQuery({
    queryKey: ["/api/venues-with-shows", debouncedFilters, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.setArtist, activePersona?.targetDmaIds],
    queryFn: async () => {
      const queryString = buildQueryParams();
      const url = `/api/venues-with-shows${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch venues");
      }
      return response.json() as Promise<VenueWithShows[]>;
    },
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const venues = allVenues || [];

  // Stage 2: Extract unique artist IDs for demographics fetch when persona is active
  const artistIdsForDemographics = useMemo(() => {
    if (!venues.length || !activePersona) return [];
    const ids = new Set<number>();
    for (const venue of venues) {
      for (const show of venue.shows) {
        if ((show.artist as any)?.id) ids.add((show.artist as any).id);
      }
    }
    return Array.from(ids);
  }, [venues, activePersona]);

  // Fetch demographics separately when persona is active (two-stage approach matching browse-artists)
  const { data: artistDemographics, isLoading: isDemographicsLoading } = useQuery({
    queryKey: ["/api/artists/demographics", artistIdsForDemographics],
    queryFn: async () => {
      if (artistIdsForDemographics.length === 0) return [];
      const response = await fetch('/api/artists/demographics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artistIds: artistIdsForDemographics }),
      });
      if (!response.ok) throw new Error('Failed to fetch artist demographics');
      return response.json();
    },
    enabled: artistIdsForDemographics.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes — demographics don't change often
    refetchOnWindowFocus: false,
  });

  // Build demographics lookup map
  const demographicsMap = useMemo(() => {
    if (!artistDemographics) return new Map();
    const map = new Map();
    for (const d of artistDemographics) {
      map.set(d.id, d);
    }
    return map;
  }, [artistDemographics]);

  // Combined loading: wait for demographics when persona is active to avoid flash of unfiltered results
  const isLoading = isVenuesLoading || (artistIdsForDemographics.length > 0 && isDemographicsLoading);

  // Filter venues and their shows using centralized filter engine
  const filteredVenuesRaw = useMemo(() => {
    if (!venues) return [];

    // Convert filters to SearchFilters format - use advancedFilters as single source of truth
    const searchFilters: SearchFilters = {
      artistName: advancedFilters.artistName,
      venueName: advancedFilters.venueName,
      genre: advancedFilters.genre && advancedFilters.genre.length > 0 ? advancedFilters.genre[0] : undefined,
      careerStage: advancedFilters.careerStage && advancedFilters.careerStage.length > 0 ? advancedFilters.careerStage : undefined,
      location: advancedFilters.location,
      dateFrom: advancedFilters.dateFrom,
      dateTo: advancedFilters.dateTo,
      venueType: advancedFilters.venueType && advancedFilters.venueType.length > 0 ? advancedFilters.venueType : undefined,
      // HARD FILTER: DMA IDs (persona target markets) - excludes shows entirely
      targetDmaIds: activePersona?.targetDmaIds && activePersona.targetDmaIds.length > 0 ? activePersona.targetDmaIds : undefined,
      personaFilters: activePersona ? {
        fanGender: activePersona.genders || [],
        fanEthnicity: activePersona.ethnicities || [],
        fanAge: activePersona.ageRanges || [],
        fanIncome: activePersona.incomeLevels || [],
        fanAttributes: activePersona.lifestyleAttributes || [],
      } : advancedFilters.targetAudience && Object.values(advancedFilters.targetAudience).some(arr => arr.length > 0) ?
        advancedFilters.targetAudience : undefined,
      minMatchPercentage: activePersona
        ? Math.max(1, activePersona.minMatchPercentage || 0)
        : advancedFilters.minMatchPercentage,
    };

    // Apply centralized filters from shared filter engine
    let filtered = applyAllFilters(venues, searchFilters);

    // Trim each venue's shows to only those matching active filters, then drop venues with 0 matches
    // This ensures counts reflect filtered results (not total shows per venue)
    const showConfig: ShowFilterConfig = {
      dateFrom: searchFilters.dateFrom,
      dateTo: searchFilters.dateTo,
      genre: searchFilters.genre,
      location: searchFilters.location,
      artistName: searchFilters.artistName,
      venueName: searchFilters.venueName,
      careerStage: searchFilters.careerStage,
      venueType: searchFilters.venueType,
    };
    filtered = trimEntityShows(filtered, showConfig);

    // Persona: merge demographics into shows, filter by audience match, drop venues with 0 remaining
    const personaTargetAudience = searchFilters.personaFilters ? {
      fanGender: searchFilters.personaFilters.fanGender || [],
      fanEthnicity: searchFilters.personaFilters.fanEthnicity || [],
      fanAge: searchFilters.personaFilters.fanAge || [],
      fanIncome: searchFilters.personaFilters.fanIncome || [],
      fanAttributes: searchFilters.personaFilters.fanAttributes || [],
    } : undefined;
    const personaMinMatch = searchFilters.minMatchPercentage || 0;

    // Pre-compute match % for all shows once (O(total_shows)) instead of per-comparison in sort
    const showMatchCache = new Map<number, number>();

    if (personaTargetAudience && demographicsMap.size > 0) {
      // Merge demographics into artist data (two-stage approach)
      filtered = filtered.map(venue => ({
        ...venue,
        shows: venue.shows.map(show => {
          const demo = demographicsMap.get((show.artist as any)?.id);
          if (demo) {
            return { ...show, artist: { ...show.artist, ...demo } };
          }
          return show;
        }),
      }));

      // Build match cache for all shows in a single pass
      for (const venue of filtered) {
        for (const show of venue.shows) {
          if (!showMatchCache.has(show.id)) {
            const artist = show.artist as any;
            const hasDemo = artist?.fanGender || artist?.fanEthnicity || artist?.fanAge || artist?.fanIncome || artist?.fanAttributes;
            if (hasDemo) {
              showMatchCache.set(show.id, calculateAudienceMatch(show.artist, personaTargetAudience).percentage);
            }
          }
        }
      }

      // Filter shows using cached values, drop venues with 0 remaining
      const effectiveMin = Math.max(1, personaMinMatch || 0);
      filtered = filtered
        .map(venue => ({
          ...venue,
          shows: venue.shows.filter(show => {
            const cached = showMatchCache.get(show.id);
            if (cached === undefined) return true; // no demographics = keep
            return cached >= effectiveMin;
          }),
        }))
        .filter(venue => venue.shows.length > 0);
    }

    // Apply venue-specific capacity filter (not in centralized engine)
    if (capacityFilter) {
      filtered = filtered.filter((venue) => {
        const capacity = venue.capacity || 0;
        switch (capacityFilter) {
          case "small":
            return capacity < 1000;
          case "medium":
            return capacity >= 1000 && capacity < 5000;
          case "large":
            return capacity >= 5000 && capacity < 20000;
          case "arena":
            return capacity >= 20000;
          default:
            return true;
        }
      });
    }

    // Pre-compute entity sort keys in a single pass (avoids recalculating per comparison)
    const entitySortKeys = new Map<number, { perfect100: number; avgMatch: number; nextShowDate: string }>();
    for (const venue of filtered) {
      let perfect100 = 0;
      let totalMatch = 0;
      let earliest = '\uffff'; // max string for comparison
      for (const show of venue.shows) {
        const pct = showMatchCache.get(show.id) ?? 0;
        if (pct === 100) perfect100++;
        totalMatch += pct;
        const d = (show.date as any as string).slice(0, 10);
        if (d < earliest) earliest = d;
      }
      entitySortKeys.set(venue.id, {
        perfect100,
        avgMatch: venue.shows.length > 0 ? totalMatch / venue.shows.length : 0,
        nextShowDate: venue.shows.length > 0 ? earliest : '\uffff',
      });
    }

    // Sort using pre-computed keys (O(N log N) with simple lookups per comparison)
    return filtered.sort((a, b) => {
      const keysA = entitySortKeys.get(a.id)!;
      const keysB = entitySortKeys.get(b.id)!;

      if (personaTargetAudience) {
        if (keysA.perfect100 !== keysB.perfect100) return keysB.perfect100 - keysA.perfect100;
        if (keysA.avgMatch !== keysB.avgMatch) return keysB.avgMatch - keysA.avgMatch;
      }

      // Then by number of shows (descending)
      if (a.shows.length !== b.shows.length) return b.shows.length - a.shows.length;

      // Then by capacity (descending)
      const aCapacity = a.capacity || 0;
      const bCapacity = b.capacity || 0;
      if (aCapacity !== bCapacity) return bCapacity - aCapacity;

      // Finally by next show date (ascending)
      return keysA.nextShowDate.localeCompare(keysB.nextShowDate);
    });
  }, [
    venues,
    capacityFilter,
    advancedFilters,
    activePersona,
    demographicsMap,
  ]);

  // Defer the output so old correctly-sorted results stay visible while new ones compute
  const filteredVenues = useDeferredValue(filteredVenuesRaw);

  // Client-side pagination
  const totalItems = filteredVenues.length;
  const totalShows = filteredVenues.reduce(
    (total, venue) => total + venue.shows.length,
    0,
  );
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVenues = filteredVenues.slice(startIndex, endIndex);

  // Debounce filters for live search while preventing focus loss during typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters({
        genre: advancedFilters.genre?.[0] || "",
        location: advancedFilters.location || "",
        dateFrom: advancedFilters.dateFrom || "",
        dateTo: advancedFilters.dateTo || "",
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [advancedFilters.genre, advancedFilters.location, advancedFilters.dateFrom, advancedFilters.dateTo]);

  // Sync basic filters with URL parameters
  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedFilters.genre) params.set('genre', debouncedFilters.genre);
    if (debouncedFilters.location) params.set('location', debouncedFilters.location);
    if (debouncedFilters.dateFrom) params.set('dateFrom', debouncedFilters.dateFrom);
    if (debouncedFilters.dateTo) params.set('dateTo', debouncedFilters.dateTo);

    // Preserve advanced filters in URL (venue name, artist name, career stage, venue type, set artist)
    if (advancedFilters.venueName) params.set('venueName', advancedFilters.venueName);
    if (advancedFilters.artistName) params.set('artistName', advancedFilters.artistName);
    if (advancedFilters.careerStage && advancedFilters.careerStage.length > 0) {
      params.set('careerStage', advancedFilters.careerStage.join(','));
    }
    if (advancedFilters.venueType && advancedFilters.venueType.length > 0) {
      params.set('venueType', advancedFilters.venueType.join(','));
    }
    if (advancedFilters.setArtist) {
      params.set('setArtist', advancedFilters.setArtist);
    }

    // Sync page number to URL (only if > 1 to keep URLs clean)
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    } else {
      params.delete('page');
    }

    const queryString = params.toString();
    const newUrl = `/browse-venues${queryString ? `?${queryString}` : ''}`;

    // Only update URL if it's different to avoid unnecessary history entries
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [debouncedFilters, advancedFilters.venueName, advancedFilters.artistName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, currentPage]);

  // Reset page to 1 when filters change (but not during initial mount period)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setCurrentPage(1);
  }, [debouncedFilters, advancedFilters.venueName, advancedFilters.artistName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, capacityFilter, activePersona]);

  const handleBasicFilterChange = (key: string, value: string) => {
    const actualValue = value === "all" ? "" : value;
    // Update advancedFilters - handle genre as array
    if (key === 'genre') {
      setAdvancedFilters(prev => ({ ...prev, genre: actualValue ? [actualValue] : [] }));
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: actualValue }));
    }
  };


  const handleBasicSearch = async () => {
    // Track search activity for achievements

    // Search is now live via debouncing - this just tracks the search activity
  };

  const handleAdvancedFiltersChange = useCallback(
    async (filters: AdvancedFilters) => {
      // Single source of truth - just update advancedFilters
      setAdvancedFilters(filters);

      // Close advanced filters dialog
      setShowAdvancedFilters(false);
    },
    [],
  );

  const clearFilters = () => {
    // Reset all filters to defaults (single source of truth)
    setAdvancedFilters(getDefaultAdvancedFilters());
    setCapacityFilter("");
    setShowFilters(false);
    setCurrentPage(1); // Reset to first page when clearing filters

    // Explicitly clear URL parameters
    window.history.replaceState({}, "", "/browse-venues");
  };

  // Handler for unified filter bar changes - updates single source of truth
  // NOTE: Must be defined before early returns to maintain consistent hooks order
  const handleUnifiedFilterChange = useCallback((key: string, value: string | string[]) => {
    setAdvancedFilters(prev => {
      // Handle genre: convert string to string[] for consistency
      if (key === 'genre' && typeof value === 'string') {
        return { ...prev, genre: value ? [value] : [] };
      }
      return { ...prev, [key]: value };
    });
  }, []);

  // Convert advancedFilters to BrowseFilters format for components
  // Include persona data when active so VenueCard filtering is consistent
  const convertedFilters = {
    ...advancedFilters,
    // Override targetAudience with persona data when a persona is active
    targetAudience: activePersona ? {
      fanGender: activePersona.genders || [],
      fanEthnicity: activePersona.ethnicities || [],
      fanAge: activePersona.ageRanges || [],
      fanIncome: activePersona.incomeLevels || [],
      fanAttributes: activePersona.lifestyleAttributes || [],
    } : advancedFilters.targetAudience,
    // Include minMatchPercentage from persona
    minMatchPercentage: activePersona
      ? Math.max(1, activePersona.minMatchPercentage || 0)
      : advancedFilters.minMatchPercentage,
  };

  const hasActiveFilters =
    (advancedFilters.genre && advancedFilters.genre.length > 0) ||
    advancedFilters.location ||
    advancedFilters.dateFrom ||
    advancedFilters.dateTo ||
    capacityFilter;

  return (
    <BrowseLayout>
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Unified Filter Bar - always mounted to prevent focus loss */}
      <UnifiedFilterBar
        currentMethod="venues"
        filters={{
          location: advancedFilters.location,
          genre: advancedFilters.genre?.[0] || '',  // Convert string[] to string for display
          dateFrom: advancedFilters.dateFrom,
          dateTo: advancedFilters.dateTo,
          artistName: advancedFilters.artistName,
          venueName: advancedFilters.venueName,
          careerStage: advancedFilters.careerStage,
          venueType: advancedFilters.venueType,
          setArtist: advancedFilters.setArtist,
        }}
        onFilterChange={handleUnifiedFilterChange}
        onClearFilters={clearFilters}
        onAdvancedFilters={() => setShowAdvancedFilters(true)}
        isAuthenticated={!!user}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">

        {/* Advanced Filters Dialog */}
        {showAdvancedFilters && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAdvancedFilters(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Advanced Filters</h2>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="rounded-full opacity-80 ring-offset-background transition-all hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-background/90 backdrop-blur-sm border shadow-lg p-1.5 hover:shadow-xl"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              <AdvancedFilterContent
                key={JSON.stringify(advancedFilters)}
                currentFilters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                browsePage="venues"
              />
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <p className="text-red-600">
              Error loading venues: {error.message}
            </p>
          </div>
        )}

        {/* Results Summary */}
        {!isLoading && !error && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Building className="h-4 w-4" />
              <span>
                {totalItems} venue{totalItems !== 1 ? "s" : ""} and {totalShows}{" "}
                show{totalShows !== 1 ? "s" : ""} found
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} size="sm">
                  <X className="h-4 w-4 mr-2" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Venues Grid */}
        {!isLoading && !error && filteredVenues.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-slate-400 dark:text-slate-500 mb-4">
              <Building className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No venues found
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              Try adjusting your search criteria or clearing filters
            </p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} className="mt-4">
                Clear All Filters
              </Button>
            )}
          </div>
        ) : !isLoading && !error ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedVenues.map((venue) => (
                <VenueCard
                  key={venue.id}
                  venue={venue}
                  currentFilters={convertedFilters}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        ) : null}

        {/* Unauthenticated User Invitation */}
        {!user && (
          <div className="mt-16 bg-gradient-to-r from-purple-500/5 via-blue-500/5 to-purple-500/5 rounded-2xl p-8 border border-purple-500/10">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full mb-6">
                <Building className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                Unlock Venue-Based Discovery
              </h2>
              <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
                Create a <strong>free MAX Platform™ account</strong> to access
                venue filtering, capacity targeting, save venues, and explore
                shows by location for strategic sponsorship planning.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mb-3">
                    <Building className="h-5 w-5 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">
                    Venue Filters
                  </h4>
                  <p className="text-sm text-neutral-600">
                    Target by capacity, type, and location
                  </p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-3">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">
                    Geographic Targeting
                  </h4>
                  <p className="text-sm text-neutral-600">
                    Find venues in your target markets
                  </p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-3">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">
                    Save & Track
                  </h4>
                  <p className="text-sm text-neutral-600">
                    Build your venue portfolio for campaigns
                  </p>
                </div>
              </div>
              <div className="flex justify-center">
                <Link href="/register">
                  <Button className="btn-max-primary px-8 py-3 text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    Create Free Account
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-neutral-500 mt-4">
                Ready to explore venues?{" "}
                <Link
                  href="/login"
                  className="text-max-brand hover:underline font-medium"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
    </BrowseLayout>
  );
}
